import { Suspense, useEffect, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";

import AvatarCharacter from "components/AvatarCharacter";
import {
  DEFAULT_VOICE_CHARACTER_ID,
  getAvatarCharacter,
} from "components/AvatarCharacter/characters";
import useAgentAudioLevel from "./useAgentAudioLevel";
import "./style.css";

function AvatarLoader() {
  return (
    <Html center>
      <div className="figure-loader">
        <span />
        Loading avatar
      </div>
    </Html>
  );
}

function CameraTarget({ target }) {
  const { camera } = useThree();

  useEffect(() => {
    camera.lookAt(...target);
    camera.updateProjectionMatrix();
  }, [camera, target]);

  return null;
}

export default function AnimatedVoiceFigure({
  call,
  audioContext,
  status = "idle",
  emotion = null,
  characterId = DEFAULT_VOICE_CHARACTER_ID,
  debug = false,
  className = "",
}) {
  const character = getAvatarCharacter(characterId);
  const [morphTargets, setMorphTargets] = useState([]);
  const speaking = status === "speaking";
  const enabled = ["connected", "listening", "thinking", "speaking"].includes(status);
  const { levelRef, diagnostics } = useAgentAudioLevel({
    call,
    audioContext,
    enabled,
    active: speaking,
    debug,
  });
  const fallbackLipSync = diagnostics.source !== "ultravox-output";
  const camera = character.voiceFraming?.camera || [0, 1.35, 3.4];
  const cameraTarget = character.voiceFraming?.target || [0, 1.35, 0];

  return (
    <div
      className={`animated-voice-figure ${className}`.trim()}
      aria-label={`${character.name} voice agent: ${status}`}
    >
      <Canvas
        camera={{ position: camera, fov: 30 }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <CameraTarget target={cameraTarget} />
        <ambientLight intensity={2.1} />
        <directionalLight position={[2.5, 4, 4]} intensity={3.4} color="#d9f6ff" />
        <directionalLight position={[-3, 2, -2]} intensity={2.2} color="#ff9876" />
        <pointLight position={[2.5, -0.5, 1.5]} intensity={7} color="#6ddcff" distance={5} />

        <Suspense fallback={<AvatarLoader />}>
          <AvatarCharacter
            character={character}
            status={status}
            emotion={emotion}
            speaking={speaking}
            levelRef={levelRef}
            fallbackLipSync={fallbackLipSync}
            onTargets={setMorphTargets}
          />
        </Suspense>
      </Canvas>

      <div className={`figure-state figure-state--${status}`}>
        <span />
        {status === "speaking"
          ? "Speaking"
          : status === "listening"
            ? "Listening"
            : status === "thinking"
              ? "Thinking"
              : status === "connecting"
                ? "Connecting"
                : "Ready"}
      </div>

      {debug && (
        <div className="figure-debug">
          character={character.id} audio={diagnostics.source}
          {" "}level={diagnostics.level.toFixed(3)} morphs={morphTargets.length}
          {diagnostics.error ? ` error=${diagnostics.error}` : ""}
        </div>
      )}
    </div>
  );
}
